/**
 * Visual effect definitions: effect types, their configs, defaults and presets.
 *
 * Moved from games' packages/shared-socket-events/src/types/effects.ts (2026-09-27)
 * so every app can use the effects engine. The game-specific parts stay in games:
 * the HNS / FPR / TRL effect events, event→effect mappings, game effect settings,
 * TRL_GLOW_COLORS and the admin request types. Games' EffectTriggerPayload is this
 * EffectTrigger plus its game event.
 *
 * Changing a config shape affects the games server (which stores admin-made
 * mappings), admin web and the Electron client: move them together, and keep
 * parsing tolerant of unknown fields.
 */

/**
 * Available effect types
 * This enum defines all visual effects that can be triggered
 */
export enum EffectType {
  /** Glowing border around the screen edges */
  BORDER_GLOW = 'border_glow',
  /** @deprecated Use TS_CONFETTI or a specific tsParticles confetti variant instead */
  CONFETTI_DEPRECATED = 'confetti',

  // tsParticles preset effects
  TS_AMBIENT             = 'ts_ambient',
  TS_BIG_CIRCLES         = 'ts_big_circles',
  TS_BUBBLES             = 'ts_bubbles',
  TS_CONFETTI            = 'ts_confetti',
  TS_CONFETTI_CANNON     = 'ts_confetti_cannon',
  TS_CONFETTI_EXPLOSIONS = 'ts_confetti_explosions',
  TS_CONFETTI_FALLING    = 'ts_confetti_falling',
  TS_CONFETTI_PARADE     = 'ts_confetti_parade',
  TS_FIRE                = 'ts_fire',
  TS_FIREFLY             = 'ts_firefly',
  TS_FIREWORKS           = 'ts_fireworks',
  TS_FOUNTAIN            = 'ts_fountain',
  TS_HYPERSPACE          = 'ts_hyperspace',
  TS_LINKS               = 'ts_links',
  TS_MATRIX              = 'ts_matrix',
  TS_METEORS             = 'ts_meteors',
  TS_PARTY               = 'ts_party',
  TS_SEA_ANEMONE         = 'ts_sea_anemone',
  TS_SNOW                = 'ts_snow',
  TS_SQUARES             = 'ts_squares',
  TS_STARS               = 'ts_stars',
  TS_TRIANGLES           = 'ts_triangles',
}

/**
 * Get display name for an effect type
 */
export function getEffectTypeDisplayName(effectType: EffectType): string {
  switch (effectType) {
    case EffectType.BORDER_GLOW:             return 'Border Glow';
    case EffectType.CONFETTI_DEPRECATED:     return 'Confetti (Deprecated)';
    case EffectType.TS_AMBIENT:             return 'Ambient';
    case EffectType.TS_BIG_CIRCLES:         return 'Big Circles';
    case EffectType.TS_BUBBLES:             return 'Bubbles';
    case EffectType.TS_CONFETTI:            return 'Confetti';
    case EffectType.TS_CONFETTI_CANNON:     return 'Confetti Cannon';
    case EffectType.TS_CONFETTI_EXPLOSIONS: return 'Confetti Explosions';
    case EffectType.TS_CONFETTI_FALLING:    return 'Confetti Falling';
    case EffectType.TS_CONFETTI_PARADE:     return 'Confetti Parade';
    case EffectType.TS_FIRE:                return 'Fire';
    case EffectType.TS_FIREFLY:             return 'Firefly';
    case EffectType.TS_FIREWORKS:           return 'Fireworks';
    case EffectType.TS_FOUNTAIN:            return 'Fountain';
    case EffectType.TS_HYPERSPACE:          return 'Hyperspace';
    case EffectType.TS_LINKS:               return 'Links';
    case EffectType.TS_MATRIX:              return 'Matrix';
    case EffectType.TS_METEORS:             return 'Meteors';
    case EffectType.TS_PARTY:               return 'Party';
    case EffectType.TS_SEA_ANEMONE:         return 'Sea Anemone';
    case EffectType.TS_SNOW:                return 'Snow';
    case EffectType.TS_SQUARES:             return 'Squares';
    case EffectType.TS_STARS:               return 'Stars';
    case EffectType.TS_TRIANGLES:           return 'Triangles';
    default: return 'Unknown Effect';
  }
}

/**
 * Get all available effect types
 */
export function getAllEffectTypes(): EffectType[] {
  return Object.values(EffectType);
}

// ============================================================================
// Effect Configurations
// ============================================================================

/**
 * Configuration for the Border Glow effect
 */
export interface BorderGlowEffectConfig {
  /** Effect type discriminator */
  type: EffectType.BORDER_GLOW;
  /** Glow color in hex format (e.g., '#ff0000') */
  color: string;
  /** Duration in milliseconds */
  durationMs: number;
  /** Glow intensity (0-1, affects opacity and spread) */
  intensity: number;
  /** Border width in pixels */
  borderWidth: number;
  /** Whether to animate/pulse the glow */
  animated: boolean;
  /**
   * How far the glow reaches inward from each edge, as a fraction of the
   * viewport (0-1). Defaults to 0.3 when omitted. Smaller values (e.g. 0.12)
   * produce a thin edge-hug; larger values a deep vignette.
   */
  depth?: number;
}

/**
 * Preset styles for confetti (provides sensible defaults)
 */
export type ConfettiPreset = 'fireworks' | 'realistic' | 'explosion' | 'pride' | 'vortex' | 'custom';

/**
 * Available confetti particle shapes
 */
export type ConfettiShape = 'square' | 'circle' | 'star';

/**
 * Origin point for confetti launch
 */
export interface ConfettiOrigin {
  /** Horizontal position (0 = left, 1 = right) */
  x: number;
  /** Vertical position (0 = top, 1 = bottom) */
  y: number;
}

/**
 * Configuration for the Confetti effect
 * Matches canvas-confetti library options exactly
 * @see https://github.com/catdad/canvas-confetti
 */
export interface ConfettiEffectConfig {
  /** Effect type discriminator */
  type: EffectType.CONFETTI_DEPRECATED;
  
  // === Effect-level settings ===
  /** Duration in milliseconds (how long the effect runs) */
  durationMs: number;
  /** Preset to use as base (individual settings override) */
  preset: ConfettiPreset;
  
  // === Canvas-confetti Options ===
  /** Number of confetti to launch per burst (1-500) */
  particleCount: number;
  /** Launch angle in degrees (0-360, 90 = straight up) */
  angle: number;
  /** Spread angle in degrees (0-360) */
  spread: number;
  /** Initial velocity in pixels (1-200) */
  startVelocity: number;
  /** Speed decay rate (0.1-1, higher = slower decay) */
  decay: number;
  /** Gravity pull (0-3, 1 = normal gravity) */
  gravity: number;
  /** Horizontal drift (-3 to 3, negative = left) */
  drift: number;
  /** Animation ticks/frames (10-1000, affects particle lifetime) */
  ticks: number;
  /** Array of hex color strings */
  colors: string[];
  /** Particle shapes to use */
  shapes: ConfettiShape[];
  /** Particle size scale (0.1-3) */
  scalar: number;
  /** Disable 3D tilt/wobble */
  flat: boolean;
  /** Launch origin point */
  origin: ConfettiOrigin;
}

/**
 * Configuration for tsParticles preset effects.
 * The preset is determined by the EffectType. Optional override fields are merged
 * on top of the preset defaults — leave undefined to use the preset's built-in value.
 */
export interface TsParticlesEffectConfig {
  type: EffectType;
  /** How long the effect runs (ms) */
  durationMs: number;
  /** Particle move speed multiplier. Undefined = use preset default. */
  speed?: number;
  /** Particle opacity (0–1). Undefined = use preset default. */
  opacity?: number;
  /** Particle size in px. Undefined = use preset default. */
  size?: number;
  /** Number of particles (for number-based presets). Undefined = use preset default. */
  particleCount?: number;
  /** Override particle colors. Empty array or undefined = use preset default. */
  colors?: string[];
}

/**
 * Union type of all effect configurations
 * Add new effect configs here as they are implemented
 */
export type EffectConfig = BorderGlowEffectConfig | ConfettiEffectConfig | TsParticlesEffectConfig;

/**
 * Default confetti colors - festive mix
 */
export const DEFAULT_CONFETTI_COLORS = [
  '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
  '#ffa500', '#ff69b4', '#ffd700', '#c0c0c0',
];

/**
 * Default configurations for each effect type
 */
export const DEFAULT_EFFECT_CONFIGS: Record<EffectType, EffectConfig> = {
  [EffectType.BORDER_GLOW]: {
    type: EffectType.BORDER_GLOW,
    color: '#ff0000',
    durationMs: 3000,
    intensity: 0.8,
    borderWidth: 20,
    animated: true,
  },
  [EffectType.CONFETTI_DEPRECATED]: {
    type: EffectType.CONFETTI_DEPRECATED,
    durationMs: 5000,
    preset: 'fireworks',
    particleCount: 50,
    angle: 90,
    spread: 360,
    startVelocity: 45,
    decay: 0.9,
    gravity: 1,
    drift: 0,
    ticks: 200,
    colors: DEFAULT_CONFETTI_COLORS,
    shapes: ['square', 'circle'],
    scalar: 1,
    flat: false,
    origin: { x: 0.5, y: 0.5 },
  },
  // tsParticles presets — burst/celebration effects (6s)
  [EffectType.TS_CONFETTI]:            { type: EffectType.TS_CONFETTI,            durationMs: 6000 },
  [EffectType.TS_CONFETTI_CANNON]:     { type: EffectType.TS_CONFETTI_CANNON,     durationMs: 6000 },
  [EffectType.TS_CONFETTI_EXPLOSIONS]: { type: EffectType.TS_CONFETTI_EXPLOSIONS, durationMs: 6000 },
  [EffectType.TS_CONFETTI_FALLING]:    { type: EffectType.TS_CONFETTI_FALLING,    durationMs: 6000 },
  [EffectType.TS_CONFETTI_PARADE]:     { type: EffectType.TS_CONFETTI_PARADE,     durationMs: 6000 },
  [EffectType.TS_FIRE]:                { type: EffectType.TS_FIRE,                durationMs: 6000 },
  [EffectType.TS_FIREWORKS]:           { type: EffectType.TS_FIREWORKS,           durationMs: 6000 },
  [EffectType.TS_FOUNTAIN]:            { type: EffectType.TS_FOUNTAIN,            durationMs: 6000 },
  [EffectType.TS_METEORS]:             { type: EffectType.TS_METEORS,             durationMs: 6000 },
  [EffectType.TS_PARTY]:               { type: EffectType.TS_PARTY,               durationMs: 6000 },
  // tsParticles presets — ambient/looping effects (8s)
  [EffectType.TS_AMBIENT]:             { type: EffectType.TS_AMBIENT,             durationMs: 8000 },
  [EffectType.TS_BIG_CIRCLES]:         { type: EffectType.TS_BIG_CIRCLES,         durationMs: 8000 },
  [EffectType.TS_BUBBLES]:             { type: EffectType.TS_BUBBLES,             durationMs: 8000 },
  [EffectType.TS_FIREFLY]:             { type: EffectType.TS_FIREFLY,             durationMs: 8000 },
  [EffectType.TS_HYPERSPACE]:          { type: EffectType.TS_HYPERSPACE,          durationMs: 8000 },
  [EffectType.TS_LINKS]:               { type: EffectType.TS_LINKS,               durationMs: 8000 },
  [EffectType.TS_MATRIX]:              { type: EffectType.TS_MATRIX,              durationMs: 8000 },
  [EffectType.TS_SEA_ANEMONE]:         { type: EffectType.TS_SEA_ANEMONE,         durationMs: 8000 },
  [EffectType.TS_SNOW]:                { type: EffectType.TS_SNOW,                durationMs: 8000 },
  [EffectType.TS_SQUARES]:             { type: EffectType.TS_SQUARES,             durationMs: 8000 },
  [EffectType.TS_STARS]:               { type: EffectType.TS_STARS,               durationMs: 8000 },
  [EffectType.TS_TRIANGLES]:           { type: EffectType.TS_TRIANGLES,           durationMs: 8000 },
};

/**
 * Preset configurations for confetti - partial overrides for the default config
 */
export const CONFETTI_PRESETS: Record<ConfettiPreset, Partial<Omit<ConfettiEffectConfig, 'type' | 'durationMs' | 'preset'>>> = {
  fireworks: {
    // Based on canvas-confetti fireworks example
    // Launches bursts from random positions every 250ms
    particleCount: 50,
    spread: 360,
    startVelocity: 30,
    decay: 0.9,
    gravity: 1,
    ticks: 60,
    origin: { x: 0.5, y: 0.3 },
  },
  realistic: {
    particleCount: 100,
    spread: 60,
    startVelocity: 45,
    decay: 0.9,
    gravity: 1,
    origin: { x: 0.5, y: 0.3 },
  },
  explosion: {
    particleCount: 200,
    spread: 360,
    startVelocity: 60,
    decay: 0.85,
    gravity: 0.8,
    origin: { x: 0.5, y: 0.5 },
  },
  pride: {
    particleCount: 150,
    spread: 180,
    startVelocity: 55,
    colors: ['#e40303', '#ff8c00', '#ffed00', '#008026', '#24408e', '#732982'],
    origin: { x: 0.5, y: 0.5 },
  },
  vortex: {
    particleCount: 75,
    spread: 45,
    startVelocity: 50,
    drift: 0.5,
    gravity: 0.5,
    ticks: 300,
    origin: { x: 0.5, y: 0.5 },
  },
  custom: {
    // No overrides - use whatever values are set
  },
};

/**
 * Apply a preset to a confetti config
 */
export function applyConfettiPreset(config: ConfettiEffectConfig, preset: ConfettiPreset): ConfettiEffectConfig {
  const presetValues = CONFETTI_PRESETS[preset];
  return {
    ...config,
    ...presetValues,
    preset,
  };
}

// ============================================================================
// Triggers
// ============================================================================

/** Plays an effect. Games' socket payload extends this with the game event that caused it. */
export interface EffectTrigger {
  /** Type of effect to display */
  effectType: EffectType;
  /** Overrides on top of the effect's defaults (applied only when config.type matches) */
  config?: Partial<EffectConfig>;
  /** Optional context about the trigger */
  context?: {
    /** Character name of the affected player (if applicable) */
    characterName?: string;
    /** Additional message to display (if applicable) */
    message?: string;
  };
}

/** Clears the active effect (only if it is of effectType, when given). */
export interface EffectClear {
  effectType?: EffectType;
}

// ============================================================================
// Type Guards and Config Resolution
// ============================================================================

/**
 * Type guard for BorderGlowEffectConfig
 */
export function isBorderGlowEffectConfig(config: EffectConfig): config is BorderGlowEffectConfig {
  return config.type === EffectType.BORDER_GLOW;
}

/**
 * Type guard for ConfettiEffectConfig
 */
export function isConfettiEffectConfig(config: EffectConfig): config is ConfettiEffectConfig {
  return config.type === EffectType.CONFETTI_DEPRECATED;
}

/**
 * Type guard for TsParticlesEffectConfig
 */
export function isTsParticlesEffectConfig(config: EffectConfig): config is TsParticlesEffectConfig {
  return typeof config.type === 'string' && (config.type as string).startsWith('ts_');
}

/**
 * Returns true for any EffectType that maps to a tsParticles preset
 */
export function isTsParticlesEffectType(type: EffectType): boolean {
  return (type as string).startsWith('ts_');
}

/** True for a well-formed trigger of a known effect type (extra fields are ignored). */
export function isEffectTrigger(data: unknown): data is EffectTrigger {
  return (
    typeof data === 'object' &&
    data !== null &&
    'effectType' in data &&
    Object.values(EffectType).includes((data as EffectTrigger).effectType)
  );
}

/**
 * Merges a partial override on top of the default config for effectType. The
 * override applies only when its discriminator matches, which guards against a
 * stale or mismatched config arriving in a trigger.
 */
export function resolveEffectConfig(effectType: EffectType, overrides?: Partial<EffectConfig>): EffectConfig {
  const base = DEFAULT_EFFECT_CONFIGS[effectType];
  if (!overrides || !('type' in overrides) || overrides.type !== effectType) return base;
  return { ...base, ...overrides } as EffectConfig;
}
